// Offline persistence and mutation queue for SwatNotes.
// Stores server data in localStorage so the app can work without connectivity,
// and queues note mutations (creates/updates/deletes) for replay on reconnect.

export interface PendingCreate {
  clientId: string;       // offline-${uuid}
  serverId?: string;      // persisted immediately after an acknowledged upload
  attempted?: boolean;   // the server may have committed even if its response was lost
  title: string;
  content_json: string;
  collection_id: string | null;
  notebook_id: string;
  note_type: string;
}

export interface PendingUpdate {
  id: string;             // may be a clientId before sync
  title: string;
  content_json: string;
  collection_id: string | null;
  title_modified: boolean;
}

export interface CachedNote extends PendingUpdate {
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  notebook_id: string | null;
  note_type: string;
}

const NOTEBOOKS_KEY = 'sn-notebooks';
const notesKey = (nbId: string) => `sn-notes-${nbId}`;
const collectionsKey = (nbId: string) => `sn-cols-${nbId}`;
const CREATES_KEY = 'sn-off-creates';
const UPDATES_KEY = 'sn-off-updates';
const DELETES_KEY = 'sn-off-deletes';

const VAULT_CACHE_KEY = 'sn-vault-active';
export function vaultCacheDisabled(): boolean { return localStorage.getItem(VAULT_CACHE_KEY) === '1'; }
export function setVaultCachePolicy(active: boolean): void {
  if (active && hasPendingOps()) throw new Error('Sync offline changes before enabling encryption.');
  localStorage.setItem(VAULT_CACHE_KEY, active ? '1' : '0');
  if (active) clearAllOfflineData();
}

function safeParse<T>(key: string): T | null {
  if (vaultCacheDisabled()) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch { return null; }
}

function safeSet(key: string, value: unknown): void {
  if (vaultCacheDisabled()) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    if ((e as DOMException)?.name === 'QuotaExceededError') {
      console.warn('swatnotes: localStorage quota exceeded, skipping cache write');
    }
  }
}

// Cache writes are optional. Mutation writes are the only durable copy of edits
// and must report failure before the UI acknowledges a save.
function saveQueue(key: string, value: unknown): void {
  if (vaultCacheDisabled()) throw new Error('Encrypted notes need a connection to save. Keep this page open.');
  localStorage.setItem(key, JSON.stringify(value));
}

function loadQueue<T>(key: string): T[] {
  if (vaultCacheDisabled()) return [];
  const raw = localStorage.getItem(key);
  if (!raw) return [];
  const value: unknown = JSON.parse(raw);
  if (!Array.isArray(value)) throw new Error('Offline queue is unreadable. Keep this page open.');
  return value as T[];
}

export function isOfflineId(id: string): boolean {
  return id.startsWith('offline-');
}

// --- Notebooks ---
export function persistNotebooks(notebooks: unknown[]): void { safeSet(NOTEBOOKS_KEY, notebooks); }
export function loadCachedNotebooks<T = unknown>(): T[] | null { return safeParse<T[]>(NOTEBOOKS_KEY); }

// --- Notes ---
export function persistNotes(nbId: string, notes: unknown[]): void { safeSet(notesKey(nbId), notes); }
export function loadCachedNotes<T = CachedNote>(nbId: string): T[] { return mergePendingNotes(nbId, safeParse<CachedNote[]>(notesKey(nbId)) ?? []) as T[]; }

// Server refreshes must not hide unsynced creates or overwrite pending edits.
// Build creates from the durable queue even when the optional note cache is full.
export function mergePendingNotes(nbId: string, notes: CachedNote[]): CachedNote[] {
  const creates = loadCreates();
  const realId = (id: string) => creates.find(c => c.clientId === id)?.serverId ?? id;
  const merged = new Map(notes.filter(n => !isOfflineId(n.id)).map(n => [n.id, n]));
  for (const create of creates.filter(c => c.notebook_id === nbId)) {
    const id = realId(create.clientId);
    if (!merged.has(id)) {
      const now = new Date().toISOString();
      merged.set(id, { ...create, id, created_at: now, updated_at: now, deleted_at: null, title_modified: false });
    }
  }
  for (const update of loadUpdates()) {
    const id = realId(update.id);
    const note = merged.get(id);
    if (note) merged.set(id, { ...note, ...update, id });
  }
  for (const id of loadDeletes()) merged.delete(realId(id));
  return [...merged.values()];
}

export function clearNotebookCache(nbId: string): void {
  localStorage.removeItem(notesKey(nbId));
  localStorage.removeItem(collectionsKey(nbId));
}

// --- Collections ---
export function persistCollections(nbId: string, collections: unknown[]): void { safeSet(collectionsKey(nbId), collections); }
export function loadCachedCollections<T = unknown>(nbId: string): T[] | null { return safeParse<T[]>(collectionsKey(nbId)); }

// --- Create queue ---
function loadCreates(): PendingCreate[] { return loadQueue<PendingCreate>(CREATES_KEY); }
function saveCreates(creates: PendingCreate[]): void { saveQueue(CREATES_KEY, creates); }
function queuedId(id: string): string {
  return loadCreates().find(c => c.serverId === id)?.clientId ?? id;
}

export function queueCreate(create: PendingCreate): void {
  const creates = loadCreates();
  creates.push(create);
  saveCreates(creates);
}

export function cancelCreate(clientId: string): void {
  // Retain the create until sync knows whether an upload is already in flight.
  queueDelete(clientId);
}

// --- Update queue ---
function loadUpdates(): PendingUpdate[] { return loadQueue<PendingUpdate>(UPDATES_KEY); }
function saveUpdates(updates: PendingUpdate[]): void { saveQueue(UPDATES_KEY, updates); }

export function queueUpdate(update: PendingUpdate): void {
  update = { ...update, id: queuedId(update.id) };
  const updates = loadUpdates();
  const idx = updates.findIndex(u => u.id === update.id);
  if (idx >= 0) updates[idx] = update;
  else updates.push(update);
  saveUpdates(updates);
}

// Remove a pending update for a note that has been successfully saved online,
// so a stale queued update cannot overwrite a more recent server-side version.
export function clearQueuedUpdate(id: string): void {
  id = queuedId(id);
  if (!loadUpdates().some(u => u.id === id)) return;
  saveUpdates(loadUpdates().filter(u => u.id !== id));
}

// --- Delete queue ---
function loadDeletes(): string[] { return loadQueue<string>(DELETES_KEY); }
function saveDeletes(ids: string[]): void { saveQueue(DELETES_KEY, ids); }

export function queueDelete(id: string): void {
  id = queuedId(id);
  const deletes = loadDeletes();
  if (!deletes.includes(id)) {
    deletes.push(id);
    saveDeletes(deletes);
  }
  saveUpdates(loadUpdates().filter(u => u.id !== id));
}

export function notePendingStatus(id: string): 'create' | 'update' | null {
  if (isOfflineId(id)) return 'create';
  id = queuedId(id);
  if (loadUpdates().some(u => u.id === id)) return 'update';
  return null;
}

export function hasPendingOps(): boolean {
  return loadCreates().length > 0 || loadUpdates().length > 0 || loadDeletes().length > 0;
}

// Clear all offline queues and per-notebook caches. Called on logout to prevent
// one user's queued data from leaking to another user on the same device.
export function clearOfflineData(notebookIds: string[]): void {
  localStorage.removeItem(CREATES_KEY);
  localStorage.removeItem(UPDATES_KEY);
  localStorage.removeItem(DELETES_KEY);
  localStorage.removeItem(NOTEBOOKS_KEY);
  for (const id of notebookIds) {
    localStorage.removeItem(notesKey(id));
    localStorage.removeItem(collectionsKey(id));
  }
}

// Clear all offline data without knowing notebook IDs — used when vault is
// configured so no plaintext note cache remains on disk.
export function clearAllOfflineData(): void {
  const prefix1 = 'sn-notes-';
  const prefix2 = 'sn-cols-';
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith(prefix1) || key.startsWith(prefix2))) toRemove.push(key);
  }
  toRemove.forEach(k => localStorage.removeItem(k));
  localStorage.removeItem(CREATES_KEY);
  localStorage.removeItem(UPDATES_KEY);
  localStorage.removeItem(DELETES_KEY);
  localStorage.removeItem(NOTEBOOKS_KEY);
}

type ApiFn = (method: string, path: string, body?: unknown) => Promise<unknown>;

// One replay per page. Acknowledgements remove only the exact submitted update,
// so newer work queued while awaiting the server remains durable.
let flushing: Promise<void> | null = null;
export function flushOfflineQueue(api: ApiFn): Promise<void> {
  if (flushing) return flushing;
  flushing = flushQueue(api).finally(() => { flushing = null; });
  return flushing;
}

async function flushQueue(api: ApiFn): Promise<void> {
  for (const create of loadCreates()) {
    if (create.serverId) continue;
    if (loadDeletes().includes(create.clientId)) {
      if (create.attempted) continue; // delete its stable server UUID below
      saveCreates(loadCreates().filter(c => c.clientId !== create.clientId));
      saveUpdates(loadUpdates().filter(u => u.id !== create.clientId));
      saveDeletes(loadDeletes().filter(id => id !== create.clientId));
      continue;
    }
    let note: { id: string };
    saveCreates(loadCreates().map(c => c.clientId === create.clientId ? { ...c, attempted: true } : c));
    try {
      note = await api('POST', '/api/notes', {
        client_id: create.clientId.slice('offline-'.length),
        title: create.title, content_json: create.content_json,
        collection_id: create.collection_id, notebook_id: create.notebook_id,
        note_type: create.note_type,
      }) as { id: string };
      if (!note || typeof note.id !== 'string' || !note.id || isOfflineId(note.id)) {
        throw new Error('Invalid response to offline note creation');
      }
    } catch (e) {
      if ((e as Error).message === 'unauthenticated') throw e;
      continue;
    }
    // Store the mapping before any further request. A later expired session must
    // not cause this acknowledged create to be uploaded a second time.
    saveCreates(loadCreates().map(c => c.clientId === create.clientId ? { ...c, serverId: note.id } : c));
  }
  const realId = (id: string) => loadCreates().find(c => c.clientId === id)?.serverId ?? id;
  for (const update of loadUpdates()) {
    const id = realId(update.id);
    if (isOfflineId(id) || loadDeletes().includes(update.id)) continue;
    try {
      const body: Record<string, unknown> = { title: update.title, content_json: update.content_json };
      if (update.title_modified) body.title_modified = true;
      await api('PUT', `/api/notes/${id}`, body);
      await api('PUT', `/api/notes/${id}/collection`, { collection_id: update.collection_id });
    } catch (e) {
      if ((e as Error).message === 'unauthenticated') throw e;
      continue;
    }
    saveUpdates(loadUpdates().filter(u => JSON.stringify(u) !== JSON.stringify(update)));
  }
  for (const pendingId of loadDeletes()) {
    const create = loadCreates().find(c => c.clientId === pendingId);
    const id = create?.serverId ?? (create?.attempted ? pendingId.slice('offline-'.length) : pendingId);
    if (isOfflineId(id)) {
      saveCreates(loadCreates().filter(c => c.clientId !== pendingId));
    } else {
      try {
        await api('DELETE', `/api/notes/${id}`);
      } catch (e) {
        if ((e as Error).message === 'unauthenticated') throw e;
        if ((e as { status?: number }).status !== 404) continue;
      }
    }
    saveUpdates(loadUpdates().filter(u => u.id !== pendingId));
    saveCreates(loadCreates().filter(c => c.clientId !== pendingId));
    saveDeletes(loadDeletes().filter(d => d !== pendingId));
  }
  // Keep mappings while any edits/deletions still refer to the temporary ID.
  saveCreates(loadCreates().filter(c => !c.serverId
    || loadUpdates().some(u => u.id === c.clientId)
    || loadDeletes().includes(c.clientId)));
}
